import React, { useEffect, useState } from 'react';
import {
  Text, View, StyleSheet, ScrollView,
} from 'react-native';
import { FontAwesome } from '@expo/vector-icons';
import Moment from 'moment';
import Colors from '../../constants/Colors';
import Style from '../../constants/Style';
import PressableStyled from '../PressableStyled';
import MeetingRoom24 from '../24pxIcons/MeetingRoom24';
import GymIcon24 from '../24pxIcons/GymIcon24';
import GetBookingsCount from '../../requests/Sensors/Occupancy/GetBookingsCount';
import { AreaType } from '../Map/SetFloorsModal/FloorItem';

interface MobileDashboardProps {
  showGraph: () => void,
  locationId: string,
  locationCapacity: number,
  liveCount: number
}

const MobileDashboard = ({
  showGraph, locationId, locationCapacity, liveCount,
}: MobileDashboardProps) => {
  const occupancy: number = 100 * Math.min(1, liveCount / locationCapacity);
  const [meetingRoomCount, setMeetingRoomCount] = useState<number | null>(null);
  const [gymCount, setGymCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setGymCount(null);
    setMeetingRoomCount(null);
    if (!locationId) return () => { };
    GetBookingsCount({
      locationId,
      locationSubtype: AreaType.GYM,
      from: Moment().startOf('day').toISOString(),
      to: Moment().endOf('day').toISOString(),
    }).then((res) => {
      if (cancelled) return;
      if (typeof res === 'number') setGymCount(res);
    });
    GetBookingsCount({
      locationId,
      locationSubtype: AreaType.MEETING_ROOM,
      from: Moment().startOf('day').toISOString(),
      to: Moment().endOf('day').toISOString(),
    }).then((res) => {
      if (cancelled) return;
      if (typeof res === 'number') setMeetingRoomCount(res);
    });
    return () => {
      cancelled = true;
    };
  }, [locationId]);

  return <ScrollView>
    <View style={styles.firstCard}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>{'Occupancy now'}</Text>
        <PressableStyled onPress={showGraph}>
          <FontAwesome
            name='angle-right'
            size={24}
            color={Colors.light.hereworksBlue500}
          />
        </PressableStyled>
      </View>
      <Text style={styles.label}>{'Current'}</Text>
      <View style={styles.countSection}>
        <Text style={styles.count}>{liveCount ?? '-'}</Text>
        <Text style={styles.peopleLabel}>{'people'}</Text>
      </View>
      <View style={Style.row}>
        <View style={styles.progressBarTrack}>
          <View style={[styles.progressBar, { width: `${occupancy}%` }]} />
        </View>
        <View style={styles.progressBarExcess}></View>
      </View>
      <View style={[styles.capacitySection, {
        width: `${100 * (styles.progressBarTrack.flex
          / (styles.progressBarTrack.flex + styles.progressBarExcess.flex))}%`,
      }]}>
        <Text style={styles.label}>{'Capacity'}</Text>
        <Text style={styles.capacityValue}>{locationCapacity ?? '-'}</Text>
      </View>
    </View>
    <Text style={styles.sectionTitle}>{'Bookings today'}</Text>
    <View style={styles.bookingsSection}>
      <View style={styles.bookingsCard}>
        <MeetingRoom24 />
        <Text style={[styles.label, styles.bookingsLabel]}>{'Meeting room'}</Text>
        <Text style={styles.count}>{meetingRoomCount ?? '-'}</Text>
      </View>
      <View style={styles.bookingsCard}>
        <GymIcon24 />
        <Text style={[styles.label, styles.bookingsLabel]}>{'Gym'}</Text>
        <Text style={styles.count}>{gymCount ?? '-'}</Text>
      </View>
    </View>
  </ScrollView>;
};

const styles = StyleSheet.create({
  firstCard: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.light.hereworksWhite100,
    backgroundColor: '#FFF',
    padding: 20,
    marginBottom: 24,
  },
  header: {
    ...Style.row,
    marginBottom: 24,
  },
  headerTitle: {
    fontSize: 18,
    lineHeight: 27,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  label: {
    fontSize: 14,
    lineHeight: 22,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  countSection: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    marginTop: 4,
    marginBottom: 16,
  },
  count: {
    fontSize: 30,
    lineHeight: 33,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
  },
  peopleLabel: {
    fontSize: 14,
    lineHeight: 14,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
    marginStart: 4,
    marginBottom: 9,
  },
  sectionTitle: {
    fontSize: 22,
    lineHeight: 30,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
    marginBottom: 16,
  },
  progressBarTrack: {
    flex: 1,
    height: 16,
    borderRadius: 100,
    backgroundColor: Colors.light.hereworksBlue50,
  },
  progressBar: {
    borderRadius: 100,
    height: '100%',
    backgroundColor: Colors.light.hereworksBlue500,
  },
  progressBarExcess: {
    marginStart: 8,
    flex: 0.3,
    height: 16,
    borderRadius: 100,
    backgroundColor: Colors.light.hereworksRed50,
  },
  capacityValue: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksDarkBlue,
  },
  capacitySection: {
    ...Style.row,
    paddingEnd: 4,
  },
  bookingsSection: {
    ...Style.row,
    marginHorizontal: -4,
  },
  bookingsCard: {
    padding: 20,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: Colors.light.hereworksWhite300,
    backgroundColor: '#FFF',
    marginHorizontal: 4,
    flex: 1,
  },
  bookingsLabel: {
    marginTop: 4,
    marginBottom: 12,
  },
});

export default MobileDashboard;
